import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { HttpError, useDataProvider, useNotify, useTranslate } from 'react-admin'

import { isRunInProgress, type SourceRun, type SourceRunStatus } from '../../data/source'
import type { ScalesDataProvider } from '../../dataProvider'
import { formatDateTime } from '../../domain/dateTime'

/** How often a run in progress is read again, while the page is open. */
export const SOURCE_RUN_POLL_MS = 3000

const STATUS_COLORS: Record<SourceRunStatus, 'default' | 'info' | 'success' | 'error'> = {
  REQUESTED: 'default',
  RUNNING: 'info',
  SUCCEEDED: 'success',
  FAILED: 'error',
}

type Notice = { kind: 'busy'; since: string } | { kind: 'refused'; message: string }

const RunSummary: FC<{ run: SourceRun }> = ({ run }) => {
  const translate = useTranslate()
  const counts = Object.entries(run.counts)
  return (
    <Stack spacing={1} data-testid="sostrat-last-run">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">
          {translate('pages.hidden.sostrat.last_run', { trigger: translate(`pages.hidden.sostrat.trigger.${run.trigger}`) })}
        </Typography>
        <Chip
          size="small"
          color={STATUS_COLORS[run.status]}
          label={translate(`pages.hidden.sostrat.status.${run.status}`)}
          data-testid="sostrat-run-status"
        />
      </Stack>
      <Typography variant="body2">
        {translate('pages.hidden.sostrat.requested_at')}: {formatDateTime(run.requestedAt)}
      </Typography>
      <Typography variant="body2" data-testid="sostrat-run-finished">
        {translate('pages.hidden.sostrat.finished_at')}: {run.finishedAt === null ? '—' : formatDateTime(run.finishedAt)}
      </Typography>
      {counts.length > 0 && (
        <Typography variant="body2" data-testid="sostrat-run-counts">
          {translate('pages.hidden.sostrat.counts')}: {counts.map(([name, count]) => `${name}: ${String(count)}`).join(' · ')}
        </Typography>
      )}
      {run.problems.length > 0 && (
        <Alert severity="error" data-testid="sostrat-run-problems">
          {translate('pages.hidden.sostrat.problems')}
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {run.problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </Box>
        </Alert>
      )}
    </Stack>
  )
}

/**
 * "Actualizar SÒSTRAT": asks `sostrat-sync` for a read, which runs on GitHub
 * Actions and takes minutes, and follows it in `source_runs` until it ends.
 * Shows the newest SÒSTRAT run, whether an admin or the hourly schedule ran it.
 */
export const SostratSyncPanel: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider<ScalesDataProvider>()
  // undefined while loading; null before the first run.
  const [run, setRun] = useState<SourceRun | null | undefined>(undefined)
  const [starting, setStarting] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)

  useEffect(() => {
    const read = { cancelled: false }
    void (async () => {
      try {
        const latest = await dataProvider.latestSostratRun()
        if (!read.cancelled) setRun(latest ?? null)
      } catch (error) {
        notify(error instanceof Error ? error.message : String(error), { type: 'error' })
      }
    })()
    return () => {
      read.cancelled = true
    }
  }, [dataProvider, notify])

  const followedId = run && isRunInProgress(run) ? run.id : null
  useEffect(() => {
    if (followedId === null) return
    const poll = { cancelled: false }
    const timer = setInterval(() => {
      void (async () => {
        const latest = await dataProvider.getSourceRun(followedId)
        if (!poll.cancelled && latest) setRun(latest)
      })()
    }, SOURCE_RUN_POLL_MS)
    return () => {
      poll.cancelled = true
      clearInterval(timer)
    }
  }, [dataProvider, followedId])

  const start = async () => {
    setStarting(true)
    setNotice(null)
    try {
      const started = await dataProvider.startSostratSync()
      if (started.kind === 'busy') setNotice({ kind: 'busy', since: started.run.requestedAt })
      setRun((await dataProvider.getSourceRun(started.run.id)) ?? null)
    } catch (error) {
      if (error instanceof HttpError && error.status === 502) {
        setNotice({ kind: 'refused', message: String(error.message) })
        // sostrat-sync recorded the refused run as failed.
        setRun((await dataProvider.latestSostratRun()) ?? null)
      } else {
        notify(error instanceof Error ? error.message : String(error), { type: 'error' })
      }
    } finally {
      setStarting(false)
    }
  }

  const inProgress = followedId !== null
  return (
    <Card variant="outlined" data-testid="sostrat-sync">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">{translate('pages.hidden.sostrat.title')}</Typography>
          <Box>
            <Button
              variant="contained"
              disabled={starting || inProgress || run === undefined}
              onClick={() => void start()}
              data-testid="sostrat-sync-button"
            >
              {run && inProgress
                ? translate('pages.hidden.sostrat.in_progress', { status: translate(`pages.hidden.sostrat.status.${run.status}`) })
                : translate('pages.hidden.sostrat.action')}
            </Button>
          </Box>
          {notice?.kind === 'busy' && (
            <Alert severity="info" data-testid="sostrat-sync-busy">
              {translate('pages.hidden.sostrat.busy', { since: formatDateTime(notice.since) })}
            </Alert>
          )}
          {notice?.kind === 'refused' && (
            <Alert severity="error" data-testid="sostrat-sync-refused">
              {translate('pages.hidden.sostrat.refused', { message: notice.message })}
            </Alert>
          )}
          {run === null && <Typography variant="body2">{translate('pages.hidden.sostrat.never')}</Typography>}
          {run && <RunSummary run={run} />}
        </Stack>
      </CardContent>
    </Card>
  )
}
